import { useAuth } from '@//providers/AuthProvider';
import { useContext, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import LogoIcon from '../Icons/LogoIcon';
import LogoutIcon from '../Icons/LogoutIcon';
import {
  AccountIcon,
  DraftsIcon,
  GameIcon,
  HelpIcon,
  HomeIcon,
  InsightsIcon,
  InviteIcon
} from '../Icons/NavIcons';
import BarLeftIcon from '../Icons/BarLeftIcon';
import roundedLogo from '@/assets/images/rounded-logo.png';
import clsx from 'clsx';
import { motion } from 'motion/react';
import { elementsTransition } from '@/utils/gamePacks';
import { LayoutContext } from './LayoutContext';
import ReactGA from 'react-ga4';

interface NavMenuProps {
  pathSegments: string[];
  isAdmin: boolean;
  isSuperAdmin: boolean;
}

export default function NavMenu({ pathSegments, isAdmin, isSuperAdmin }: NavMenuProps) {
  const { signOut, organization } = useAuth();
  const navigate = useNavigate();
  const [activeItem, setActiveItem] = useState('');
  const { navbarCollapsed, setNavbarCollapsed } = useContext(LayoutContext);

  useEffect(() => {
    if (pathSegments.length === 1 && pathSegments[0] === 'dashboard') {
      setActiveItem('home');
    }

    if (pathSegments.length > 1 && pathSegments[1] === 'host') {
      setActiveItem('host');
    }

    if (pathSegments.length > 1 && pathSegments[1] === 'insights') {
      setActiveItem('insights');
    }

    if (
      (pathSegments.length > 1 && pathSegments[1] === 'admin') ||
      (pathSegments.length === 4 && pathSegments[3] === 'users') ||
      (pathSegments.length === 2 && pathSegments[1] === 'users')
    ) {
      setActiveItem('admin');
    }

    if (pathSegments.length > 1 && pathSegments[1] === 'getting-started') {
      setActiveItem('help');
    }

    if (pathSegments.length > 1 && pathSegments[1] === 'profile') {
      setActiveItem('profile');
    }
  }, [pathSegments]);

  const handleExpand = () => {
    setNavbarCollapsed(!navbarCollapsed);
  };

  const handleCreateGame = () => {
    ReactGA.event({
      category: 'Navigation',
      action: 'Clicked Link',
      label: 'Create a Game'
    });
    navigate('/dashboard/host');
  };

  const handleHelp = () => {
    ReactGA.event({
      category: 'Navigation',
      action: 'Clicked Link',
      label: 'Help'
    });
    navigate('/dashboard/getting-started');
  };

  const handleInsights = () => {
    ReactGA.event({
      category: 'Navigation',
      action: 'Clicked Link',
      label: 'Insights'
    });
    navigate('/dashboard/insights');
  };

  return (
    <motion.div
      className={clsx('px-20 pb-20 h-full flex flex-col justify-between shrink-0 @container')}
      variants={{
        expanded: { width: 320 },
        collapsed: { width: 100 }
      }}
      animate={navbarCollapsed ? 'collapsed' : 'expanded'}
      transition={elementsTransition}
    >
      <div className="@4xs:p-18 @max-4xs:p-14">
        <Link to="/dashboard" className="flex items-center">
          <LogoIcon className={`${'text-black'} w-80 @max-4xs:hidden`} />
          <img src={roundedLogo} className="size-32 @4xs:hidden" />
        </Link>
      </div>
      <div className="nav mt-60 grow-1">
        <Link
          to="/dashboard/"
          className={`nav-link ${activeItem === 'home' && 'nav-link-active'} @4xs:p-16 @max-4xs:py-16 @max-4xs:px-18`}
        >
          <HomeIcon active={activeItem === 'home'} className="@4xs:mr-12 size-24 min-w-24" />
          <motion.span
            variants={{
              expanded: { opacity: 1, width: 200 },
              collapsed: { opacity: 0, width: 0 }
            }}
            animate={navbarCollapsed ? 'collapsed' : 'expanded'}
            transition={elementsTransition}
            className="overflow-hidden text-nowrap"
          >
            Home
          </motion.span>
        </Link>
        <a
          onClick={handleCreateGame}
          className={`nav-link ${activeItem === 'host' && 'nav-link-active'} @4xs:p-16 @max-4xs:py-16 @max-4xs:px-18`}
        >
          <GameIcon active={activeItem === 'host'} className="@4xs:mr-12 size-24 min-w-24" />
          <motion.span
            variants={{
              expanded: { opacity: 1, width: 200 },
              collapsed: { opacity: 0, width: 0 }
            }}
            animate={navbarCollapsed ? 'collapsed' : 'expanded'}
            transition={elementsTransition}
            className="overflow-hidden text-nowrap"
          >
            Create a Game
          </motion.span>
        </a>
        <a
          onClick={handleInsights}
          className={`nav-link ${activeItem === 'insights' && 'nav-link-active'} @4xs:p-16 @max-4xs:py-16 @max-4xs:px-18`}
        >
          <InsightsIcon
            active={activeItem === 'insights'}
            className="@4xs:mr-12 size-24 min-w-24"
          />
          <motion.span
            variants={{
              expanded: { opacity: 1, width: 200 },
              collapsed: { opacity: 0, width: 0 }
            }}
            animate={navbarCollapsed ? 'collapsed' : 'expanded'}
            transition={elementsTransition}
            className="overflow-hidden text-nowrap"
          >
            Game Insights
          </motion.span>
        </a>
        {(isAdmin || isSuperAdmin) && (
          <Link
            to={isSuperAdmin ? '/dashboard/admin' : `/dashboard/users`}
            className={`nav-link ${activeItem === 'admin' && 'nav-link-active'} @4xs:p-16 @max-4xs:py-16 @max-4xs:px-18`}
          >
            <DraftsIcon active={activeItem === 'admin'} className="@4xs:mr-12 size-24 min-w-24" />
            <motion.span
              variants={{
                expanded: { opacity: 1, width: 200 },
                collapsed: { opacity: 0, width: 0 }
              }}
              animate={navbarCollapsed ? 'collapsed' : 'expanded'}
              transition={elementsTransition}
              className="overflow-hidden text-nowrap"
            >
              Admin
            </motion.span>
          </Link>
        )}
        {isAdmin && organization?.onFreeTrial && (
          <Link to={'/invite'} className={`nav-link @4xs:p-16 @max-4xs:py-16 @max-4xs:px-18`}>
            <InviteIcon className="@4xs:mr-12 size-24 min-w-24" />
            <motion.span
              variants={{
                expanded: { opacity: 1, width: 200 },
                collapsed: { opacity: 0, width: 0 }
              }}
              animate={navbarCollapsed ? 'collapsed' : 'expanded'}
              transition={elementsTransition}
              className="overflow-hidden text-nowrap"
            >
              Invite Teammates
            </motion.span>
          </Link>
        )}
      </div>
      <div className="nav">
        <a
          onClick={handleHelp}
          className={`nav-link ${activeItem === 'help' && 'nav-link-active'} @4xs:p-16 @max-4xs:py-16 @max-4xs:px-18`}
        >
          <HelpIcon active={activeItem === 'help'} className="@4xs:mr-12 size-24 min-w-24" />
          <motion.span
            variants={{
              expanded: { opacity: 1, width: 200 },
              collapsed: { opacity: 0, width: 0 }
            }}
            animate={navbarCollapsed ? 'collapsed' : 'expanded'}
            transition={elementsTransition}
            className="overflow-hidden text-nowrap"
          >
            Help & Support
          </motion.span>
        </a>
        <Link
          to="/dashboard/profile"
          className={`nav-link ${activeItem === 'profile' && 'nav-link-active'} @4xs:p-16 @max-4xs:py-16 @max-4xs:px-18`}
        >
          <AccountIcon active={activeItem === 'profile'} className="@4xs:mr-12 size-24 min-w-24" />
          <motion.span
            variants={{
              expanded: { opacity: 1, width: 200 },
              collapsed: { opacity: 0, width: 0 }
            }}
            animate={navbarCollapsed ? 'collapsed' : 'expanded'}
            transition={elementsTransition}
            className="overflow-hidden text-nowrap"
          >
            Manage Account
          </motion.span>
        </Link>
        <button
          onClick={signOut}
          className="nav-link @4xs:p-16 @max-4xs:py-16 @max-4xs:px-18 text-left"
        >
          <LogoutIcon className="@4xs:mr-12 size-24 min-w-24" />
          <motion.span
            variants={{
              expanded: { opacity: 1, width: 200 },
              collapsed: { opacity: 0, width: 0 }
            }}
            animate={navbarCollapsed ? 'collapsed' : 'expanded'}
            transition={elementsTransition}
            className="overflow-hidden text-nowrap"
          >
            Logout
          </motion.span>
        </button>
        <button
          onClick={handleExpand}
          className="nav-link @4xs:p-16 @max-4xs:py-16 @max-4xs:px-18 w-fit"
        >
          <BarLeftIcon />
        </button>
      </div>
    </motion.div>
  );
}
